import { ReactElement, useCallback, useMemo } from 'react'
import {
  createLineBasicMaterialMemoized,
  createMeshStandardMaterialMemoized,
  getShapeEdges,
  getShapeMatrixTransform,
  getShapeObject,
} from '@scene'
import { ColorRepresentation, DoubleSide } from 'three'
import shallow from 'zustand/shallow'
import { useTheme } from '@mui/material'
import { BasicMesh, sceneConfig } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useAppStore } from '@stores'
import { useDisposeThreeResourceOnUnmount } from '@editorHooks'
import { useModelStore } from 'src/components/pages/Editor/stores/modelStore'
import { useSceneStore } from 'src/components/pages/Editor/stores/sceneStore'
import { extrudeMember } from './utils'

interface Props {
  data: PositionMember
  outlines?: boolean
  onClick?: (event: ThreeEvent<MouseEvent>) => void
  isSelected?: boolean
  isHighlighted?: boolean
  visible?: boolean
  /** Cross-section height (z) of the extruded member. Defaults to a thin placeholder. */
  height?: number
  /** Cross-section width (y) of the extruded member. Defaults to `height`. */
  width?: number
  /**
   * Guid reported as the mesh `name` for click / marquee selection. Defaults to `data.guid`.
   * Slab beams pass their surface guid here so selection happens in surface space, while colour
   * and transparency stay keyed on the beam guid (`data.guid`).
   */
  selectionGuid?: string
}

const defaultPositionThickness = 0.15

/** Below this mesh opacity, outlines look wrong (LineBasicMaterial vs mesh opacity); hide them. */
const OUTLINE_OPACITY_THRESHOLD = 0.6

/** Same rules as the slab-beam mesh body (guid override, then `slab_beams` type). */
export function useSlabBeamMeshIsTransparent(beamGuid: string): boolean {
  const translucent = useModelStore(state => state.translucent)
  const { visibilityByType: transparencyVisibilityByType, guidToTransparent } = useModelStore(
    state => state.transparencyVisibilityModel,
    shallow,
  )
  return useMemo(() => {
    if (!translucent) return false
    const isTransparentForGuid = guidToTransparent[beamGuid]
    if (isTransparentForGuid !== undefined) return isTransparentForGuid
    return transparencyVisibilityByType.slab_beams
  }, [beamGuid, guidToTransparent, translucent, transparencyVisibilityByType.slab_beams])
}

const PositionMemberMeshElement = ({
  data,
  outlines = true,
  onClick,
  isSelected = false,
  isHighlighted = false,
  visible = true,
  height = defaultPositionThickness,
  width = height,
  selectionGuid,
}: Props): ReactElement => {
  const theme = useTheme()

  // state picking due to performance reasons
  const mappedColor = useSceneStore(
    useCallback(state => state.guidToColor[data.guid], [data.guid]),
  ) as ColorRepresentation | undefined

  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)

  const points = extrudeMember(data.member, height, width)

  const matrixTransform = useMemo(() => getShapeMatrixTransform(points), [points])

  const shapeObject = useMemo(
    () => getShapeObject(points, [], matrixTransform),
    [points, matrixTransform],
  )

  const edges = useMemo(() => getShapeEdges(shapeObject, height), [shapeObject, height])

  useDisposeThreeResourceOnUnmount(edges)

  const isTransparent = useSlabBeamMeshIsTransparent(data.guid)

  const opacity = useMemo(() => {
    if (isTransparent) return transparentOpacity
    return 1
  }, [isTransparent, transparentOpacity])

  const showOutlineGeometry = outlines && opacity >= OUTLINE_OPACITY_THRESHOLD

  const material = useMemo(
    () =>
      createMeshStandardMaterialMemoized({
        side: DoubleSide,
        color: isHighlighted
          ? theme.scenePalette.selection
          : mappedColor || theme.scenePalette.elements3d.member,
        transparent: true,
        opacity,
      }),
    [opacity, mappedColor, isHighlighted, theme],
  )

  useDisposeThreeResourceOnUnmount(material)

  const outlinesMaterial = useMemo(
    () =>
      createLineBasicMaterialMemoized({
        color: isSelected ? theme.scenePalette.highlightDark : theme.scenePalette.outlines,
        opacity,
      }),
    [theme, isSelected, opacity],
  )

  useDisposeThreeResourceOnUnmount(outlinesMaterial)

  return (
    <BasicMesh
      name={selectionGuid ?? data.guid}
      matrix={matrixTransform}
      matrixAutoUpdate={false}
      onClick={onClick}
      cursor={onClick ? 'pointer' : undefined}
      userData={{
        guid: data.guid,
        elementType: 'members',
        positionType: data.position_type,
      }}
      visible={visible}
      material={material}
    >
      <extrudeGeometry
        args={[
          shapeObject,
          {
            depth: height,
            bevelEnabled: false,
          },
        ]}
      />

      {showOutlineGeometry && (
        <lineSegments
          layers={sceneConfig.R3FNonSelectableObjectLayer}
          geometry={edges}
          renderOrder={100}
          visible={visible}
          material={outlinesMaterial}
        ></lineSegments>
      )}
    </BasicMesh>
  )
}

export default PositionMemberMeshElement
